import { FormPageInputItem } from 'backend';
import { AssortedDrillDownProperties, convertValuesForDrillDown } from 'models';
import { getReEnterPropertyId } from 'reEnter';
import {
  DateProperty,
  FormPage,
  FormPageInputData,
  FormPageInputItemMailAddress,
  FormPageInputItemPasswordHash,
  FormPageInputItemText,
  MailAddressProperty,
  MailRefusalFlagProperty,
  MonthDayProperty,
  MultipleSelectionProperty,
  PasswordHashProperty,
  PROPERTY_TYPE,
  SingleSelectionProperty,
  TextProperty,
} from 'shared';
import { convertDateInitialValue } from './convertDateInitialValue';
import { convertFileUploadInitialValue } from './convertFileUploadInitialValue';
import { convertMailAddressInitialValue } from './convertMailAddressInitialValue';
import { convertMailRefusalFlagInitialValue } from './convertMailRefusalFlagInitialValue';
import { convertMonthDayInitialValue } from './convertMonthDayInitialValue';
import { convertMultipleSelectionInitialValue } from './convertMultipleSelectionInitialValue';
import { convertNumberInitialValue } from './convertNumberInitialValue';
import { convertPasswordHashInitialValue } from './convertPasswordHashInitialValue';
import { convertSingleSelectionInitialValue } from './convertSingleSelectionInitialValue';
import { convertTextInitialValue } from './convertTextInitialValue';

type PropertyCouldHaveReEnter =
  | MailAddressProperty
  | TextProperty
  | PasswordHashProperty;

const getInitialValueConverter =
  <T extends PropertyCouldHaveReEnter, I extends FormPageInputItem>(
    converter: (property: T, formPageInputItem?: FormPageInputItem) => I,
  ) =>
  (
    propertyId: string,
    property: T,
    formPageInputData?: FormPageInputData,
  ): FormPageInputData => {
    const { reEnter } = property;
    const inputItem = formPageInputData && formPageInputData[propertyId];
    if (!reEnter) {
      return {
        [propertyId]: converter(property, inputItem),
      };
    }
    const reEnterPropertyId = getReEnterPropertyId(propertyId);
    const reEnterInputItem =
      formPageInputData && formPageInputData[reEnterPropertyId];
    return {
      [propertyId]: converter(property, inputItem),
      [reEnterPropertyId]: converter(property, reEnterInputItem),
    };
  };

const getMailAddressInitialValues = getInitialValueConverter<
  MailAddressProperty,
  FormPageInputItemMailAddress
>(convertMailAddressInitialValue);

const getTextInitialValues = getInitialValueConverter<
  TextProperty,
  FormPageInputItemText
>(convertTextInitialValue);

const getPasswordHashInitialValues = getInitialValueConverter<
  PasswordHashProperty,
  FormPageInputItemPasswordHash
>(convertPasswordHashInitialValue);

type ConvertInitialValuesPayload = {
  properties: FormPage['properties'];
  formPageInputData?: FormPageInputData;
  assortedDrillDownProperties: AssortedDrillDownProperties;
};

export const convertInitialValues = ({
  properties,
  formPageInputData,
  assortedDrillDownProperties,
}: ConvertInitialValuesPayload): FormPageInputData => {
  const pageInputData = formPageInputData || {};
  if (!properties) {
    return pageInputData;
  }
  const inputData = Object.entries(properties).reduce(
    (acc, [propertyId, property]) => {
      switch (property.propertyType) {
        case PROPERTY_TYPE.mailAddress:
          return {
            ...acc,
            ...getMailAddressInitialValues(
              propertyId,
              property as MailAddressProperty,
              formPageInputData,
            ),
          };
        case PROPERTY_TYPE.text: {
          return {
            ...acc,
            ...getTextInitialValues(
              propertyId,
              property as TextProperty,
              formPageInputData,
            ),
          };
        }
        case PROPERTY_TYPE.number: {
          const inputItem = formPageInputData && formPageInputData[propertyId];
          return {
            ...acc,
            [propertyId]: convertNumberInitialValue(property, inputItem),
          };
        }
        case PROPERTY_TYPE.date: {
          const inputItem = formPageInputData && formPageInputData[propertyId];
          return {
            ...acc,
            [propertyId]: convertDateInitialValue(
              property as DateProperty,
              inputItem,
            ),
          };
        }
        case PROPERTY_TYPE.monthDay: {
          const inputItem = formPageInputData && formPageInputData[propertyId];
          return {
            ...acc,
            [propertyId]: convertMonthDayInitialValue(
              property as MonthDayProperty,
              inputItem,
            ),
          };
        }
        case PROPERTY_TYPE.singleSelection: {
          const inputItem = formPageInputData && formPageInputData[propertyId];
          return {
            ...acc,
            [propertyId]: convertSingleSelectionInitialValue(
              property as SingleSelectionProperty,
              inputItem,
            ),
          };
        }
        case PROPERTY_TYPE.multipleSelection: {
          const inputItem = formPageInputData && formPageInputData[propertyId];
          return {
            ...acc,
            [propertyId]: convertMultipleSelectionInitialValue(
              property as MultipleSelectionProperty,
              inputItem,
            ),
          };
        }
        case PROPERTY_TYPE.mailRefusalFlag: {
          const inputItem = formPageInputData && formPageInputData[propertyId];
          return {
            ...acc,
            [propertyId]: convertMailRefusalFlagInitialValue(
              property as MailRefusalFlagProperty,
              inputItem,
            ),
          };
        }
        case PROPERTY_TYPE.passwordHash: {
          return {
            ...acc,
            ...getPasswordHashInitialValues(
              propertyId,
              property,
              formPageInputData,
            ),
          };
        }
        case PROPERTY_TYPE.fileUpload: {
          const inputItem = formPageInputData && formPageInputData[propertyId];
          return {
            ...acc,
            [propertyId]: convertFileUploadInitialValue(property, inputItem),
          };
        }
        case PROPERTY_TYPE.authentication: {
          // TODO 認証項目 or 照合項目
          return {};
        }
        case PROPERTY_TYPE.staticContent: {
          return acc;
        }
        default: {
          const props: never = property;
          console.error(props);
          return acc;
        }
      }
    },
    {} as FormPageInputData,
  );

  return convertValuesForDrillDown({
    assortedDrillDownProperties,
    formPageInputData: inputData,
  });
};
